"use client";

import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";
import { cn } from "../utils";

type ToastType = "success" | "error" | "warning" | "info";

interface Toast {
    id: string;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => { } });

export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: ToastType = "success") => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setToasts((prev) => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 4000);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const icons = {
        success: <CheckCircle className="h-5 w-5 text-green-500" />,
        error: <XCircle className="h-5 w-5 text-red-500" />,
        warning: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
        info: <Info className="h-5 w-5 text-blue-500" />,
    };

    const borderColors = {
        success: "border-l-green-500",
        error: "border-l-red-500",
        warning: "border-l-yellow-500",
        info: "border-l-blue-500",
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div
              className="fixed bottom-20 right-4 left-4 sm:bottom-6 sm:right-6 sm:left-auto z-[100] space-y-3 sm:max-w-sm"
              role="region"
              aria-label="Notifications"
              aria-live="polite"
              aria-atomic="false"
            >
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        role="alert"
                        className={cn(
                            "flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-card-foreground shadow-premium border-l-4 animate-in slide-in-from-right duration-300",
                            borderColors[toast.type]
                        )}
                    >
                        {icons[toast.type]}
                        <p className="flex-1 text-sm text-foreground">{toast.message}</p>
                        <button
                          onClick={() => removeToast(toast.id)}
                          className="text-muted-foreground/50 hover:text-muted-foreground"
                          aria-label="Dismiss notification"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
